import { convertAbsoluteDistanceToFeetAndInches } from '../../../components/markup/utils/helpers'
import { Label, Select } from '../../lib/toolBoxes/2D'

/**
 * A header beam is a label group (rectangle + length text) that shares its data with the beam path.
 * Multi-selecting the beam pulls the path out of its label group, so the group has to be looked up
 * through the Select tool, which remembers it as the path's parent.
 */
export type HeaderLabel = {
    pathItem: paper.Path
    labelGroup: paper.Group
}

const getLabelTexts = (labelGroup: paper.Item) =>
    (labelGroup.children ?? []).filter((child) => child.className === 'PointText') as paper.PointText[]

/**
 * Finds the label group of a multi-selected header beam. The group has to be resolved before awaiting anything,
 * clearing the multi-selection puts the path back and forgets the group.
 */
export const findMultiSelectedHeaderLabel = (selectTool: Select, openingId: number): HeaderLabel | null => {
    const pathItem = selectTool
        .getMultiSelectedItems()
        ?.find((item) => item.className === 'Path' && item.data.drawable_id === openingId)

    if (!pathItem) return null

    const labelGroup = selectTool.getMultiSelectedItemParent(pathItem.id)

    // a group without a parent was removed from the canvas (or is the layer, when the beam has no label at all)
    if (!labelGroup || labelGroup.parent === null || getLabelTexts(labelGroup).length !== 1) return null

    return { pathItem: pathItem as paper.Path, labelGroup: labelGroup as paper.Group }
}

/**
 * Shows the quantity as feet and inches in the label of a header beam. Works whether or not the path
 * is currently part of its label group.
 */
export const updateHeaderLabelText = (
    labelTool: Label,
    { labelGroup, pathItem }: HeaderLabel,
    quantity: number | null | undefined
) => {
    const labelTexts = getLabelTexts(labelGroup)

    if (labelTexts.length !== 1) {
        console.warn('Header label group does not contain exactly one point text')

        return
    }

    const [pointText] = labelTexts
    const rectangle = labelGroup.children.find((child) => child.className === 'Path' && child !== pathItem)
    const horizontalPadding = rectangle ? rectangle.bounds.width - pointText.bounds.width : 0
    const { feet, inches } = convertAbsoluteDistanceToFeetAndInches(quantity ?? 0)

    labelTool.updateLabelText(pointText, `${feet}' ${inches}''`)

    if (!rectangle) return

    // new content grows from the left edge of the text, so re-fit the rectangle around it and re-center the text
    const center = rectangle.bounds.center.clone()

    if (horizontalPadding > 0) {
        rectangle.bounds.width = pointText.bounds.width + horizontalPadding
    }

    rectangle.position = center
    pointText.position = center
}
